import { useState, useEffect } from "react";
import api from "../lib/api";
import { X, Check } from "lucide-react";

/**
 * Weekly 1-question mood check-in card.
 *
 * Self-contained: fetches its own prompt, renders 5 emoji buttons, and
 * submits via POST /api/staff-pulse/submit. Silently no-ops if the
 * endpoint returns anything unexpected (e.g. tenant without the feature).
 */
export default function PulseCard({ userName, compact = false }) {
  const [pulse, setPulse] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [note, setNote] = useState("");
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/staff-pulse/prompt")
      .then(r => { if (alive) setPulse(r.data); })
      .catch(() => { if (alive) setPulse(null); });
    return () => { alive = false; };
  }, []);

  if (!pulse || dismissed) return null;

  const submit = async (mood) => {
    setSubmitting(true);
    try {
      await api.post("/staff-pulse/submit", { mood, note: note.trim() });
      setPulse(p => ({ ...p, should_show: false, already_submitted: true, submitted_mood: mood }));
      setNote("");
    } catch {
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
  };

  // Already logged — tiny confirmation strip
  if (pulse.already_submitted) {
    return (
      <div data-testid="pulse-done-card" className="flex items-center gap-2 text-xs text-slate-400 font-['Manrope']">
        <Check className="w-3.5 h-3.5 text-emerald-500" />
        <span>
          Pulse logged for {pulse.week_of}
          {pulse.submitted_mood && ` · ${(pulse.mood_emoji || {})[pulse.submitted_mood] || ""} ${(pulse.mood_labels || {})[pulse.submitted_mood] || ""}`}
        </span>
        <button onClick={() => setDismissed(true)} className="ml-auto text-slate-300 hover:text-slate-500">
          <X className="w-3 h-3" />
        </button>
      </div>
    );
  }

  if (!pulse.should_show) return null;

  return (
    <div
      data-testid="pulse-card"
      className={`bg-white border border-slate-200 rounded-2xl relative overflow-hidden ${compact ? "p-4" : "p-5"}`}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-rose-50/40 via-white to-amber-50/40 pointer-events-none" />
      <div className="relative">
        <div className="flex items-start justify-between mb-1">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-rose-500 font-bold font-['Manrope']">
              Weekly Pulse · {pulse.week_of}
            </p>
            <h3 className="font-['Outfit'] text-lg text-slate-900 mt-1">
              How's your week going{userName ? `, ${userName.split(" ")[0]}` : ""}?
            </h3>
          </div>
          <button
            onClick={() => setDismissed(true)}
            data-testid="pulse-dismiss-btn"
            className="text-slate-300 hover:text-slate-500 p-1 -mt-1"
            aria-label="Dismiss">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-xs text-slate-400 font-['Manrope'] mb-4">
          One tap — managers see aggregate trends, not individual responses unless you add a note.
        </p>
        <div className="flex justify-between gap-2 mb-3">
          {[1, 2, 3, 4, 5].map(m => (
            <button
              key={m}
              data-testid={`pulse-mood-${m}`}
              disabled={submitting}
              onClick={() => submit(m)}
              className="flex-1 flex flex-col items-center gap-1 py-3 rounded-xl bg-white border border-slate-200 hover:border-rose-300 hover:bg-rose-50/60 hover:scale-[1.03] transition-all disabled:opacity-50">
              <span className="text-2xl leading-none">
                {(pulse.mood_emoji || {})[m] || "•"}
              </span>
              <span className="text-[10px] text-slate-500 font-['Manrope'] font-medium">
                {(pulse.mood_labels || {})[m] || ""}
              </span>
            </button>
          ))}
        </div>
        <input
          data-testid="pulse-note-input"
          value={note}
          onChange={e => setNote(e.target.value.slice(0, 500))}
          placeholder="Optional note (only visible to management)"
          className="w-full h-9 border border-slate-200 rounded-lg text-sm bg-white/70 px-3 outline-none focus:border-rose-300"
        />
      </div>
    </div>
  );
}
